import { Badge } from '@buyerflo/ui/components/Badge'
import { Card } from '@buyerflo/ui/components/Card'
import { useQuery } from '@tanstack/react-query'
import Loading from 'components/Loading'
import { fmtAud, fmtDate } from 'affiliate/shared'
import { SizableText, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'

const typeLabel: Record<string, string> = {
    accrual: 'Commission earned',
    clawback: 'Reversal',
    payout: 'Paid out',
    adjustment: 'Adjustment',
}

export default function Earnings() {
    const trpc = useTRPC()
    const query = useQuery(trpc.affiliate.getMyEarnings.queryOptions())

    if (query.isLoading) return <Loading />
    const data = query.data
    if (!data) return <SizableText>Could not load earnings.</SizableText>

    return (
        <YStack gap="$4">
            <XStack gap="$3" flexWrap="wrap">
                <Card padded flex={1} minWidth={150}>
                    <SizableText fontSize={11} textTransform="uppercase" letterSpacing={0.4} color="$colorMuted">Pending payout</SizableText>
                    <SizableText fontSize={22} fontWeight="700">{fmtAud(data.unpaidCents)}</SizableText>
                </Card>
                <Card padded flex={1} minWidth={150}>
                    <SizableText fontSize={11} textTransform="uppercase" letterSpacing={0.4} color="$colorMuted">Paid to date</SizableText>
                    <SizableText fontSize={22} fontWeight="700">{fmtAud(data.paidToDateCents)}</SizableText>
                </Card>
                <Card padded flex={1} minWidth={150}>
                    <SizableText fontSize={11} textTransform="uppercase" letterSpacing={0.4} color="$colorMuted">Lifetime</SizableText>
                    <SizableText fontSize={22} fontWeight="700">{fmtAud(data.lifetimeCents)}</SizableText>
                </Card>
            </XStack>

            <Card inset>
                <XStack
                    paddingHorizontal="$4"
                    paddingVertical="$2.5"
                    borderBottomWidth={1}
                    borderBottomColor="$borderColor"
                    gap="$3"
                >
                    <SizableText flex={2} size="$1" color="$colorMuted">Type</SizableText>
                    <SizableText flex={2} size="$1" color="$colorMuted" display="none" $sm={{ display: 'flex' }}>Detail</SizableText>
                    <SizableText flex={1.2} size="$1" color="$colorMuted">Date</SizableText>
                    <SizableText flex={1.2} size="$1" color="$colorMuted">Amount</SizableText>
                </XStack>

                {data.entries.length === 0 && (
                    <View padding="$4">
                        <SizableText color="$colorMuted">No earnings yet.</SizableText>
                    </View>
                )}

                {data.entries.map(e => (
                    <XStack
                        key={e.id}
                        paddingHorizontal="$4"
                        paddingVertical="$3"
                        gap="$3"
                        alignItems="center"
                        borderBottomWidth={1}
                        borderBottomColor="$borderColor"
                    >
                        <View flex={2}>
                            <Badge tone={e.amountCents >= 0 ? 'ok' : 'danger'}>{typeLabel[e.type] ?? e.type}</Badge>
                        </View>
                        <SizableText flex={2} size="$2" color="$colorMuted" display="none" $sm={{ display: 'flex' }}>{e.note ?? ''}</SizableText>
                        <SizableText flex={1.2} size="$2" color="$colorMuted">{fmtDate(e.createdAt)}</SizableText>
                        <SizableText flex={1.2} fontWeight="600" color={e.amountCents >= 0 ? '$statusOk' : '$statusDanger'}>{fmtAud(e.amountCents)}</SizableText>
                    </XStack>
                ))}
            </Card>
        </YStack>
    )
}
